import * as React from "react";
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import { InputAdornment, } from "@material-ui/core";

import clsx from 'clsx';
import { defaultInputProps, useRootStyles } from "./globalprops"
import { Loading } from "./Loading";
import { ICommonMultiSelectControlsWithLabel } from "./ICommonInputsControls";
import { useTranslation } from 'react-i18next';



export interface IOption {
    value: string;
    label?: string;
    enabled?: boolean;
}

interface IExtendedOption<T extends IOption> {
    isFromSelectedList: boolean;
    value: T;
}

interface IProps<T extends IOption> {
    freeSolo?: boolean;
}

export type AllProps<T extends IOption> = IProps<T> & ICommonMultiSelectControlsWithLabel<T>;

export const MultiSelect = <T extends IOption>(props: AllProps<T>) => {
    const classes = useRootStyles(useTheme());
    const { t } = useTranslation('common');
    
    // Default input props
    const [selectedItems, setSelectedItems] = React.useState<T[]>((props.value || []).map(selectedValue => {
            const matchingValue = props.options.find(value => value.value === selectedValue.value);
            return matchingValue || selectedValue; // Use the full value object if it exists in the valueList
        }));
    const [valueList, setValueList] = React.useState<IExtendedOption<T>[]>(props.options.map(value => ({value: value, isFromSelectedList: false})));
    
    React.useEffect(() => {
        // Refresh selectedItems when props.value changes
        setSelectedItems((props.value || []).map(selectedValue => {
            const matchingValue = props.options.find(value => value.value === selectedValue.value);
            return matchingValue || selectedValue;
        }));
    }, [props.value, props.options]);

    React.useEffect(() => {
        // Refresh valueList when props.options changes
        setValueList(props.options.map(value => ({ value: value, isFromSelectedList: false })));
    }, [props.options]);

    // Validator function to check if the value is valid
    const validateValue = (newValue: T[] | null): [boolean, string] => {
        // Return incoming error
        if (props.error) return props.error; 

        // Check if the value is required
        if (props.required && (newValue?.length || 0) === 0) return [true, t('xIsRequired', {x: props.label})];

        // Process external validation or return default
        return props.onValidate && props.onValidate(newValue)|| [false, ""]
    }
    const [error, setError] = React.useState(props.error || [false, ""]);

    // Update the selected items and validate the value
    const onUpdate = (newValue: T[]) => {
        setSelectedItems(newValue);
        const validationError = validateValue(newValue);
        setError(validationError);
        props.onChange && props.onChange(validationError[0] ? null : newValue, validationError[0] === false);
    }

    // Prevalidate the value when the component mounts
    React.useEffect(() => {
        props.prevalidate && onUpdate(selectedItems);
    }, []);

    // Create error message for the input
    const errorStuff = error && error[0] ? ({
        error: true,
        helperText: error[1]
    }) : undefined;

    // Add selected values that are not in the value list
    selectedItems.forEach(selectedItem => {
        if (!valueList.some(value => value.value.value == selectedItem.value)) {
            setValueList([...valueList, {value: {...selectedItem, label: selectedItem.label ?? selectedItem.value, enabled: props.isUnknownValuesEnabled ?? false}, isFromSelectedList: true}]);
        }
    });

    const valueT:T[] = valueList.map(value => value.value);

    return (props.loading ? <Loading label={props.label ?? ""} /> : <Autocomplete
        className={classes.root}
        multiple
        freeSolo={props.freeSolo}
        disabled={props.disabled}
        options={valueT}
        getOptionLabel={(option) => {
            // When freeSolo is enabled, option can be a string
            if (typeof option === 'string') return option;
            return option.label || option.value;
        }}
        getOptionDisabled={(option) => {
            // Strings don't have enabled property
            if (typeof option === 'string') return false;
            return option.enabled === false;
        }}
        value={selectedItems}
        onChange={(event, newValue) => {
            // When freeSolo is enabled, newValue can contain strings
            if (props.freeSolo) {
                const processedValue = newValue.map(item => {
                    if (typeof item === 'string') {
                        // Convert string to IOption object
                        return { value: item, label: item } as T;
                    }
                    return item;
                });
                onUpdate(processedValue);
            } else {
                onUpdate(newValue as T[]);
            }
        }}

        renderInput={(params) => (
          <TextField
            {...params}
            {...defaultInputProps}
            {...errorStuff}
            label={`${props.label}${props.required ? " *" : ""}`}
            placeholder={props.placeholder || (props.freeSolo ? `${t('select')} of typ...` : `${t('select')}...`)}

          />
        )}
      />)
}

export default MultiSelect;